import nextAPI, {
  couponApiUseSession as couponApi,
  gameAPIUseSession as gameApi,
  parseAPI,
} from '../app/axios';
import config from '../app/config';
import { simpleHash } from '../app/utils';

export default class MainService {
  static async crawl(url) {
    const urlHash = simpleHash(url);
    const listRawData = localStorage.getItem('goods_url-list');

    if (!listRawData) {
      localStorage.setItem('goods_url-list', '{}');
    }

    if (listRawData) {
      const list = JSON.parse(listRawData);
      const goodsData = list[urlHash];

      // cache 7 days
      if (
        goodsData &&
        Math.floor(Date.now()) <
          Math.floor(Date.parse(goodsData.cacheDate) + 604800000)
      ) {
        return {
          title: goodsData?.title,
          subtitle: goodsData?.subtitle,
          imageUrl: goodsData?.imageUrl,
          buttons: goodsData?.buttons,
        };
      }
    }

    const response = await parseAPI.get(`${config.META_TAG_CRAWLER_URL}`, {
      params: {
        url,
      },
    });

    localStorage.setItem(
      'goods_url-list',
      JSON.stringify({
        ...JSON.parse(listRawData),
        [urlHash]: {
          title: response?.result?.title,
          subtitle: response?.result?.description,
          imageUrl: response?.result?.image?.url,
          buttons: response?.result?.buttons,
          cacheDate: new Date().toISOString(),
        },
      }),
    );

    return {
      title: response?.result?.title,
      subtitle: response?.result?.description,
      imageUrl: response?.result?.image?.url,
      buttons: response?.result?.buttons,
    };
  }

  /**
   *
   * @param {Array<String>} tagNames
   * @param {String} orgId - required
   * @param {String} customerId - optional
   * @param {object} options - optional
   */
  static async findTagV3(
    tagName = '',
    orgId,
    customerId,
    options,
    output = ['name', 'count'],
  ) {
    if (!orgId) {
      throw new Error('findTag: orgId undefined');
    }
    const query = {
      where: {
        organization: {
          className: 'Organization',
          objectId: orgId,
        },
        customer: customerId
          ? {
              className: 'Customer',
              objectId: customerId,
            }
          : undefined,
        name: {
          $options: 'i',
          $regex: tagName,
        },
      },
      sort: { lastUsed: -1 },
      limit: 10,
      ...options,
      options: {
        ...options?.options,
        output,
      },
    };
    const response = await parseAPI.post('/functions/findTagV3', query);
    return response.result;
  }

  static async getOrganizationMembers({
    org,
    keyword,
    memberId,
    memberIds,
    limit,
  }) {
    const mainQuery = {
      order: 'firstName',
      limit,
      _method: 'GET',
    };

    if (org) {
      mainQuery.where = {
        $or: [
          {
            $relatedTo: {
              object: {
                __type: 'Pointer',
                className: 'Membership',
                objectId: org.members.objectId,
              },
              key: 'users',
            },
          },
        ],
      };
    }

    if (keyword) {
      mainQuery.where = {
        $or: [
          {
            $relatedTo: {
              object: {
                __type: 'Pointer',
                className: 'Membership',
                objectId: org.members.objectId,
              },
              key: 'users',
            },
            firstName: {
              $regex: keyword,
              $options: 'i',
            },
          },
          {
            $relatedTo: {
              object: {
                __type: 'Pointer',
                className: 'Membership',
                objectId: org.members.objectId,
              },
              key: 'users',
            },
            lastName: {
              $regex: keyword,
              $options: 'i',
            },
          },
        ],
      };
    }

    if (memberId) {
      mainQuery.where = { objectId: memberId };
    }

    if (memberIds) {
      mainQuery.where = { objectId: { $in: memberIds } };
    }

    if (limit) {
      mainQuery.limit = limit;
    }

    const { results } = await parseAPI.post('/classes/_User', mainQuery);
    return results;
  }

  static async getCouponList(orgId) {
    if (!orgId) {
      throw new Error('getCouponList: orgId undefined');
    }

    const response = await couponApi.get(`/coupon/api/v1/couponSet/${orgId}`, {
      orgId,
    });

    return response?.data?.couponList || [];
  }

  static async getGameList(orgId) {
    if (!orgId) {
      throw new Error('getGameList: orgId undefined');
    }

    const response = await gameApi.get(
      `/campaign?organizationId=${orgId}&onlyActive=true`,
    );

    return response?.campaignList || [];
  }

  static async getService(orgId, provider) {
    const response = await nextAPI.get(`/services/${orgId}/${provider}`);
    return response;
  }

  static async getTriggerBot(orgId) {
    const response = await nextAPI.get(
      `/bot/flows/${orgId}?filter=trigger&type=trigger`,
    );
    return response?.flows || {};
  }
}
